import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Car, Calculator, CheckCircle2, Clock3, FileCheck2, ShieldCheck, Truck, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';
import { PORTAL_LINKS } from '../constants/site';

const ASSET_TYPES = [
  { icon: Car, title: 'Caravans & Camper Trailers', desc: 'Touring caravans, pop-tops, hard-floor and soft-floor camper trailers.' },
  { icon: Truck, title: 'Motorhomes & RVs', desc: 'Class B/C motorhomes, campervans and recreational vehicles for travel.' },
  { icon: Wrench, title: 'Boats & PWC', desc: 'Fishing boats, ski boats, cruisers, jet skis and personal watercraft.' },
  { icon: Car, title: 'Motorbikes', desc: 'Road bikes, touring bikes and eligible recreational motorcycles.' },
  { icon: Truck, title: 'ATVs & Side-by-Sides', desc: 'Eligible ATV and UTV/side-by-side applications (consumer or business use).' },
  { icon: Wrench, title: 'Leisure Trailers', desc: 'Boat trailers and recreational trailers that meet lender policy.' },
];

const FAQs = [
  {
    q: 'What loan sizes are typically available for leisure finance?',
    a: 'Leisure finance applications commonly start from around $5,000. Typical approved amounts often sit in the $5,000 to $150,000 range, and some lenders may consider higher limits for strong profiles and eligible assets.',
  },
  {
    q: 'What terms can I choose?',
    a: 'Terms are commonly between 2 and 7 years, depending on asset type, condition, and your profile. Some scenarios may allow shorter or longer structures subject to lender policy.',
  },
  {
    q: 'Do I need a deposit?',
    a: 'Not always. Some lenders can support low-deposit or no-deposit scenarios for strong applicants, while other files may require a contribution to improve approval and pricing.',
  },
  {
    q: 'Can I finance a used caravan, boat, or motorbike?',
    a: 'Yes, many lenders finance used leisure assets. Asset age, kilometres/hours, condition and resale profile all influence eligibility and final terms.',
  },
  {
    q: 'How fast can I get a decision?',
    a: 'Straightforward files can move quickly, sometimes within one business day. More complex applications (private sale, older asset, credit complexity) can take longer.',
  },
];

const TESTIMONIALS = [
  {
    quote: 'Family caravan purchase with balanced repayments and an optional deposit to reduce monthly cost.',
    title: 'Scenario: Caravan upgrade',
  },
  {
    quote: 'Used wake boat application where lender policy on asset age and private-sale checks determined final structure.',
    title: 'Scenario: Used boat acquisition',
  },
  {
    quote: 'Motorhome purchase with 5-year term comparison and rate-tier review before settlement.',
    title: 'Scenario: Motorhome fit-out',
  },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value);

export default function LeisureFinancePage() {
  const [assetPrice, setAssetPrice] = useState(75000);
  const [deposit, setDeposit] = useState(10000);
  const [termYears, setTermYears] = useState(5);
  const [rate, setRate] = useState(8.24);

  const loanAmount = Math.max(assetPrice - deposit, 0);

  const monthlyRepayment = useMemo(() => {
    if (!loanAmount || !termYears || !rate) return 0;
    const monthlyRate = rate / 100 / 12;
    const months = termYears * 12;
    if (monthlyRate === 0) return loanAmount / months;
    return (loanAmount * monthlyRate * (1 + monthlyRate) ** months) / (((1 + monthlyRate) ** months) - 1);
  }, [loanAmount, termYears, rate]);

  return (
    <div className="min-h-screen bg-[#FAF8FF]" data-testid="leisure-finance-page">
      <SEO
        title="Leisure Finance Australia | Caravans, Boats, Motorhomes & Recreational Assets"
        description="Compare leisure finance pathways for caravans, camper trailers, motorhomes, boats, jet skis, motorbikes and more. Check eligibility, indicative ranges and repayments."
        keywords="leisure finance australia, caravan finance, motorhome finance, boat finance, jet ski finance, motorbike finance"
      />

      <section className="relative overflow-hidden border-b border-purple-100 bg-gradient-to-br from-white via-purple-50 to-blue-50" data-testid="leisure-finance-hero-section">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-[1.2fr_0.9fr] lg:px-8 lg:py-20">
          <div data-testid="leisure-finance-hero-content">
            <span className="inline-flex items-center rounded-full border border-purple-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-purple-700" data-testid="leisure-finance-hero-pill">
              Leisure Finance
            </span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight text-[#170F2D] sm:text-5xl lg:text-6xl" data-testid="leisure-finance-hero-heading">
              Finance your next leisure asset with clearer options.
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#4A4556] sm:text-base" data-testid="leisure-finance-hero-description">
              We help Australian borrowers compare lender pathways for caravans, camper trailers, motorhomes, boats, jet skis, motorbikes and more. Indicative structures often range from approximately <strong>$5,000 to $150,000</strong> with common terms between <strong>2 and 7 years</strong>, subject to lender approval and individual circumstances.
            </p>

            <div className="mt-7 flex flex-wrap gap-3" data-testid="leisure-finance-hero-cta-row">
              <a href={PORTAL_LINKS.LEISURE} data-testid="leisure-finance-hero-primary-link">
                <Button className="rounded-full bg-purple-600 px-7 py-6 text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid="leisure-finance-hero-primary-cta">
                  Check Your Options
                  <ArrowRight size={15} className="ml-2" />
                </Button>
              </a>
              <a href={PORTAL_LINKS.LEISURE} data-testid="leisure-finance-hero-secondary-link">
                <Button variant="outline" className="rounded-full border-purple-300 bg-white px-7 py-6 text-xs font-bold uppercase tracking-[0.14em] text-purple-700 hover:bg-purple-50" data-testid="leisure-finance-hero-secondary-cta">
                  See What You Can Get Approved For
                </Button>
              </a>
            </div>

            <p className="mt-4 text-xs text-[#6E6782]" data-testid="leisure-finance-hero-disclaimer">
              Indicative rates from approximately 6.24% p.a. for strong profiles. Comparison rate examples and final pricing vary by lender, asset type, and credit profile.
            </p>
          </div>

          <div className="rounded-2xl border border-purple-100 bg-white p-6 shadow-sm" data-testid="leisure-finance-calculator-card">
            <div className="mb-4 flex items-center gap-2">
              <Calculator size={18} className="text-purple-600" />
              <h2 className="text-lg font-semibold text-[#1B1431]" data-testid="leisure-finance-calculator-heading">Quick repayment estimate</h2>
            </div>

            <div className="space-y-4" data-testid="leisure-finance-calculator-inputs">
              <label className="block" data-testid="leisure-finance-price-control">
                <div className="mb-2 flex items-center justify-between text-sm text-[#4A4556]">
                  <span>Asset price</span>
                  <span className="font-semibold text-[#1B1431]" data-testid="leisure-finance-price-value">{formatCurrency(assetPrice)}</span>
                </div>
                <input data-testid="leisure-finance-price-slider" type="range" min={5000} max={250000} step={1000} value={assetPrice} onChange={(e) => setAssetPrice(Number(e.target.value))} className="w-full accent-purple-600" />
              </label>

              <label className="block" data-testid="leisure-finance-deposit-control">
                <div className="mb-2 flex items-center justify-between text-sm text-[#4A4556]">
                  <span>Deposit</span>
                  <span className="font-semibold text-[#1B1431]" data-testid="leisure-finance-deposit-value">{formatCurrency(deposit)}</span>
                </div>
                <input data-testid="leisure-finance-deposit-slider" type="range" min={0} max={100000} step={1000} value={deposit} onChange={(e) => setDeposit(Math.min(Number(e.target.value), assetPrice))} className="w-full accent-purple-600" />
              </label>

              <label className="block" data-testid="leisure-finance-term-control">
                <div className="mb-2 flex items-center justify-between text-sm text-[#4A4556]">
                  <span>Loan term</span>
                  <span className="font-semibold text-[#1B1431]" data-testid="leisure-finance-term-value">{termYears} years</span>
                </div>
                <input data-testid="leisure-finance-term-slider" type="range" min={2} max={7} step={1} value={termYears} onChange={(e) => setTermYears(Number(e.target.value))} className="w-full accent-purple-600" />
              </label>

              <label className="block" data-testid="leisure-finance-rate-control">
                <div className="mb-2 flex items-center justify-between text-sm text-[#4A4556]">
                  <span>Indicative rate</span>
                  <span className="font-semibold text-[#1B1431]" data-testid="leisure-finance-rate-value">{rate.toFixed(2)}% p.a.</span>
                </div>
                <input data-testid="leisure-finance-rate-slider" type="range" min={6.24} max={13.85} step={0.01} value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-full accent-purple-600" />
              </label>
            </div>

            <div className="mt-5 rounded-xl border border-purple-100 bg-purple-50 p-4" data-testid="leisure-finance-calculator-results">
              <div className="flex items-center justify-between text-sm text-[#4A4556]">
                <span>Estimated loan amount</span>
                <strong data-testid="leisure-finance-loan-amount">{formatCurrency(loanAmount)}</strong>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm text-[#4A4556]">
                <span>Estimated monthly repayment</span>
                <strong className="text-purple-700" data-testid="leisure-finance-monthly-repayment">{formatCurrency(Math.round(monthlyRepayment))}</strong>
              </div>
              <p className="mt-3 text-xs text-[#6E6782]" data-testid="leisure-finance-calculator-footnote">
                Estimate only. Repayments and comparison rate depend on lender fees, approved structure, and your individual profile.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14" data-testid="leisure-finance-assets-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-base font-semibold uppercase tracking-[0.16em] text-purple-700" data-testid="leisure-finance-assets-eyebrow">Eligible asset categories</h2>
          <p className="mt-3 max-w-3xl text-sm text-[#4A4556] sm:text-base" data-testid="leisure-finance-assets-description">
            We support mainstream Australian leisure categories commonly considered by lenders.
          </p>

          <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="leisure-finance-assets-grid">
            {ASSET_TYPES.map((asset) => (
              <article key={asset.title} className="rounded-2xl border border-purple-100 bg-white p-5" data-testid={`leisure-finance-asset-card-${asset.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="mb-3 inline-flex rounded-xl bg-purple-100 p-2 text-purple-700">
                  <asset.icon size={20} />
                </div>
                <h3 className="text-lg font-semibold text-[#1B1431]">{asset.title}</h3>
                <p className="mt-2 text-sm text-[#4A4556]">{asset.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-purple-100 bg-white py-14" data-testid="leisure-finance-rates-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-base font-semibold uppercase tracking-[0.16em] text-purple-700" data-testid="leisure-finance-rates-eyebrow">Indicative ranges & policy notes</h2>
          <div className="mt-5 grid gap-4 lg:grid-cols-3" data-testid="leisure-finance-rates-cards">
            <div className="rounded-2xl border border-purple-100 bg-[#FAF8FF] p-5" data-testid="leisure-finance-range-loan-size-card">
              <p className="text-sm text-[#6E6782]">Typical loan size</p>
              <p className="mt-2 text-2xl font-bold text-[#1B1431]">$5,000 to $150,000</p>
              <p className="mt-2 text-sm text-[#4A4556]">Some lender pathways can support higher limits for strong profiles and eligible assets.</p>
            </div>
            <div className="rounded-2xl border border-purple-100 bg-[#FAF8FF] p-5" data-testid="leisure-finance-range-term-card">
              <p className="text-sm text-[#6E6782]">Common term range</p>
              <p className="mt-2 text-2xl font-bold text-[#1B1431]">2 to 7 years</p>
              <p className="mt-2 text-sm text-[#4A4556]">Final term depends on asset age, usage profile, and lender policy at approval.</p>
            </div>
            <div className="rounded-2xl border border-purple-100 bg-[#FAF8FF] p-5" data-testid="leisure-finance-range-rate-card">
              <p className="text-sm text-[#6E6782]">Indicative rates</p>
              <p className="mt-2 text-2xl font-bold text-[#1B1431]">From ~6.24% p.a.</p>
              <p className="mt-2 text-sm text-[#4A4556]">Many approved files price above headline rates based on risk tier and total application profile.</p>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-purple-100 bg-purple-50 p-4 text-xs leading-relaxed text-[#5A5470]" data-testid="leisure-finance-comparison-disclaimer">
            <strong>Comparison rate disclaimer:</strong> Comparison rates vary by lender and include interest plus most fees and charges on a standardised loan example. Your disclosed comparison rate and repayments may differ based on asset type, loan size, term, risk tier and approved fee structure. All applications are subject to lender approval and responsible lending checks.
          </div>
        </div>
      </section>

      <section className="py-14" data-testid="leisure-finance-eligibility-section">
        <div className="mx-auto grid w-full max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <h2 className="text-base font-semibold uppercase tracking-[0.16em] text-purple-700" data-testid="leisure-finance-eligibility-eyebrow">Eligibility snapshot</h2>
            <p className="mt-3 text-sm text-[#4A4556] sm:text-base" data-testid="leisure-finance-eligibility-description">
              Most lenders assess affordability, asset suitability and profile quality together.
            </p>
            <ul className="mt-5 space-y-3" data-testid="leisure-finance-eligibility-list">
              {[
                'You are 18+ and meet Australian residency/visa requirements accepted by the lender.',
                'The asset is eligible by age, condition, and valuation policy.',
                'Your credit profile and repayment capacity support the requested structure.',
                'For business-use scenarios, ABN history and trading evidence may be required.',
                'Private-sale and interstate purchases can be supported subject to extra checks.',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-[#4A4556]">
                  <CheckCircle2 size={18} className="mt-0.5 text-purple-700" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-purple-100 bg-white p-6" data-testid="leisure-finance-how-it-works-card">
            <h3 className="text-xl font-semibold text-[#1B1431]" data-testid="leisure-finance-how-it-works-heading">How it works</h3>
            <div className="mt-5 space-y-4" data-testid="leisure-finance-how-it-works-steps">
              {[
                { icon: FileCheck2, title: '1. Start your quote', copy: 'Share asset details, target repayment range and your preferred term.' },
                { icon: ShieldCheck, title: '2. We assess lender fit', copy: 'Your profile is matched to lender policy and risk appetite.' },
                { icon: Clock3, title: '3. Review options', copy: 'Compare structure, rate range and repayment outcomes before proceeding.' },
                { icon: Wrench, title: '4. Settle and enjoy', copy: 'Complete docs, settle funds and get on the road or water faster.' },
              ].map((step) => (
                <div key={step.title} className="flex items-start gap-3">
                  <span className="rounded-lg bg-purple-100 p-2 text-purple-700">
                    <step.icon size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-[#1B1431]">{step.title}</p>
                    <p className="text-sm text-[#4A4556]">{step.copy}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-purple-100 bg-white py-14" data-testid="leisure-finance-testimonials-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-base font-semibold uppercase tracking-[0.16em] text-purple-700" data-testid="leisure-finance-testimonials-eyebrow">Typical borrower scenarios</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3" data-testid="leisure-finance-testimonials-grid">
            {TESTIMONIALS.map((item) => (
              <article key={item.title} className="rounded-2xl border border-purple-100 bg-[#FAF8FF] p-5" data-testid={`leisure-finance-testimonial-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <p className="text-sm text-[#4A4556]">“{item.quote}”</p>
                <p className="mt-4 text-sm font-semibold text-[#1B1431]">{item.title}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14" data-testid="leisure-finance-faq-section">
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-base font-semibold uppercase tracking-[0.16em] text-purple-700" data-testid="leisure-finance-faq-eyebrow">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-6 space-y-3" data-testid="leisure-finance-faq-accordion">
            {FAQs.map((item, index) => (
              <AccordionItem key={item.q} value={`faq-${index}`} className="rounded-xl border border-purple-100 bg-white px-5" data-testid={`leisure-finance-faq-item-${index + 1}`}>
                <AccordionTrigger className="text-left text-sm font-semibold text-[#1B1431] hover:text-purple-700">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-[#4A4556]">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="border-t border-purple-100 bg-gradient-to-r from-[#1D1238] to-[#341E63] py-14 text-white" data-testid="leisure-finance-final-cta-section">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold sm:text-4xl" data-testid="leisure-finance-final-heading">Ready to plan your leisure finance options?</h2>
          <p className="mt-4 max-w-2xl text-sm text-white/85 sm:text-base" data-testid="leisure-finance-final-description">
            Start your quote today and compare lender pathways aligned to your asset, budget and timing.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-testid="leisure-finance-final-cta-row">
            <a href={PORTAL_LINKS.LEISURE} data-testid="leisure-finance-final-primary-link">
              <Button className="rounded-full bg-white px-8 py-6 text-xs font-bold uppercase tracking-[0.14em] text-purple-700 hover:bg-purple-50" data-testid="leisure-finance-final-primary-cta">
                Check Your Options
              </Button>
            </a>
            <Link to="/car-loans" data-testid="leisure-finance-final-secondary-link">
              <Button variant="outline" className="rounded-full border-white/60 bg-transparent px-8 py-6 text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-white/10" data-testid="leisure-finance-final-secondary-cta">
                Explore Car Finance
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
